/* import __COLOCATED_TEMPLATE__ from './cost-code-to-credit-table.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service'
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { PAGINATION_DEFAULT_COUNT_PER_PAGE } from 'sh-www/constants/routes/reports';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import { COMPANY_PROFILE_COMPONENT } from 'sh-www/components/company-profile/company-profile';
import { showModal, closeModal } from 'sh-www/utils/modal-controls';

const CLASS_NAME = 'SH-c-cost-code-to-credit-table';
const ROUTE_CLASS_NAME = 'SH-r-reports-company-credits';

const EMPTY_CELL_TEXT = '—';

class ReportsCostCodeToCreditTable extends Component {
  @service router;
  @service session;

  #costCodeTableRows = createCache(() => {
    return this.args.costCodes
      .reduce((accum, costCode) => {
        if (costCode.availableCreditInCents || costCode.pendingCreditInCents) {
          accum.push(costCode);
        }

        return accum;
      }, [])
      .sort((a, b) => a.body.localeCompare(b.body));
  });

  get costCodeOptions() {
    return this.args.costCodes
      .sort((a, b) => a.body.localeCompare(b.body));
  }

  get className() {
    return CLASS_NAME;
  }

  get routeClassName() {
    return ROUTE_CLASS_NAME;
  }

  get company() {
    return this.args.company || this.session.currentUser.company;
  }

  get emptyCellText() {
    return EMPTY_CELL_TEXT;
  }

  get costCodeTableRowsPaginated() {
    const startIndex = (this.args.costCodesCurrentPage - 1) * PAGINATION_DEFAULT_COUNT_PER_PAGE;
    const endIndex = startIndex + PAGINATION_DEFAULT_COUNT_PER_PAGE;

    return this.costCodeTableRows.slice(startIndex, endIndex);
  }

  get addCreditDisabled() {
    return !this.args.costCodes.length;
  }

  get transferCreditDisabled() {
    const { costCodes } = this.args;
    if (!costCodes) {
      return false;
    }

    return costCodes.every(({ availableCreditInCents }) => availableCreditInCents <= 0);
  }

  get total() {
    return this.costCodeTableRows.length;
  }

  get totalPages() {
    return Math.ceil(this.total / PAGINATION_DEFAULT_COUNT_PER_PAGE);
  }

  get previousPage() {
    return this.args.costCodesCurrentPage - 1;
  }

  get nextPage() {
    return this.args.costCodesCurrentPage + 1;
  }

  get disablePreviousPage() {
    return this.args.costCodesCurrentPage <= 1;
  }

  get disableNextPage() {
    return this.args.costCodesCurrentPage >= this.totalPages;
  }

  get pageControlsDisplayString() {
    const { total } = this;
    const currentPage = this.args.costCodesCurrentPage;

    const displayPageMin = (currentPage - 1) * PAGINATION_DEFAULT_COUNT_PER_PAGE + 1;
    const displayPageMax = Math.min(currentPage * PAGINATION_DEFAULT_COUNT_PER_PAGE, total);

    return `Showing ${displayPageMin} - ${displayPageMax} of ${total}`;
  }

  get costCodeTableRows() {
    return getValue(this.#costCodeTableRows);
  }

  get emptyStateTitle() {
    return this.session.currentUser.isAdmin
      ? 'Company does not have any credits'
      : 'You do not have any credits';
  }

  get costCodeToMenuOptionsMap() {
    const { currentUser } = this.session;
    return this.args.costCodes
      .reduce((accum, costCode) => {
        accum[costCode.id] = [];
        if (currentUser.superAdmin && costCode.availableCreditInCents) {
          accum[costCode.id].push({
            title: 'Apply to subscription',
            onClick: () => this.onApplyToSubscription(costCode),
          });
        }

        return accum;
      }, {});
  }

  get hasMenuOptions() {
    return Object.values(this.costCodeToMenuOptionsMap).some((menuOptions) => menuOptions.length > 0);
  }

  @action
  onAddCreditClicked() {
    const { company, costCodeOptions } = this;

    showModal(this, 'reports/add-credit-modal', {
      costCodes: costCodeOptions,
      company,
      loadCompanyCostCodeCreditsTask: this.args.loadCompanyCostCodeCreditsTask,
      loadCreditUnpaidInvoicesTask: this.args.loadCreditUnpaidInvoicesTask,
      loadCompanyCreditFactoidsTask: this.args.loadCompanyCreditFactoidsTask,
      updateCompanyCostCodeCredits: this.args.updateCompanyCostCodeCredits,
      updateCompanyCreditFactoids: this.args.updateCompanyCreditFactoids,
      updateShGeneralUnpaidInvoices: this.args.updateShGeneralUnpaidInvoices,
      onManageCodesClicked: this.onManageCodesClicked,
    });
  }

  @action
  onApplyToSubscription(defaultCostCode) {
    const { company, costCodeOptions } = this;

    showModal(this, 'reports/apply-credit-to-subscription-modal', {
      defaultCostCode,
      costCodes: costCodeOptions,
      company,
      loadCompanyCostCodeCreditsTask: this.args.loadCompanyCostCodeCreditsTask,
      loadCompanyCreditFactoidsTask: this.args.loadCompanyCreditFactoidsTask,
      updateCompanyCostCodeCredits: this.args.updateCompanyCostCodeCredits,
      updateCompanyCreditFactoids: this.args.updateCompanyCreditFactoids,
    });
  }

  @action
  onTransferCreditClicked() {
    const { company, costCodeOptions } = this;

    showModal(this, 'reports/transfer-credit-modal', {
      costCodes: costCodeOptions,
      company,
      loadCompanyCostCodeCreditsTask: this.args.loadCompanyCostCodeCreditsTask,
      updateCompanyCostCodeCredits: this.args.updateCompanyCostCodeCredits,
      onManageCodesClicked: this.onManageCodesClicked,
    });
  }

  @action
  onManageCodesClicked() {
    // our willTransition in the application route wont catch tab change (query param)
    // need to manually close the modal that calls this
    closeModal(this);

    const companyId = this.company.id;
    this.router.transitionTo(EMBER_ROUTE_NAMES.COMPANY_PROFILE, companyId, {
      queryParams: {
        section: COMPANY_PROFILE_COMPONENT.SECTIONS.CODES,
      },
    });
  }
}

export default ReportsCostCodeToCreditTable;
